(() => {
  if (window.__ckMarketAwareImageInit) return;
  window.__ckMarketAwareImageInit = true;

  const IMAGE_SELECTOR = '[data-market-aware-image]';

  // Hide all market-aware images initially (works for both snippet and regular img tags)
  // Force hide to prevent any flicker from initial render
  const hideInitialImages = () => {
    document.querySelectorAll('[data-market-aware-image-target]').forEach((img) => {
      img.style.opacity = '0';
      img.style.transition = 'opacity 0.3s ease-in-out';
    });
  };

  // Hide immediately before DOM is ready to prevent any visible flash
  hideInitialImages();

  /**
   * Normalize image URL for comparison (remove query params, hash, protocol, etc.)
   */
  const normalizeImageUrl = (url) => {
    if (!url) return '';
    try {
      // Handle protocol-relative URLs (//example.com)
      let normalizedUrl = url;
      if (normalizedUrl.startsWith('//')) {
        normalizedUrl = 'https:' + normalizedUrl;
      }
      const urlObj = new URL(normalizedUrl);
      // Return pathname only (without protocol, domain, query, hash)
      return urlObj.pathname;
    } catch {
      // If URL parsing fails, try to extract path before query/hash
      return url
        .split('?')[0]
        .split('#')[0]
        .replace(/^\/\//, '')
        .replace(/^https?:/, '');
    }
  };

  /**
   * Get target image based on market and language
   * Logic:
   * - UK market: Always use default image (ignore language)
   * - EU market:
   *   - If Spanish (es): Use eu-es if available, else default
   *   - If English (en) or other: Use eu-en if available, else default
   */
  const getTargetImage = (market, imageDefault, imageEuEn, imageEuEs) => {
    switch (market) {
      case 'uk':
        return imageDefault;
      case 'eu':
      default: {
        // Get current language from URL (via utils.js)
        const currentLang = window.__ckGetCurrentLang ? window.__ckGetCurrentLang() : 'en';

        // For Spanish, prefer eu-es, fallback to default
        if (currentLang === 'es') {
          return imageEuEs && imageEuEs.trim() !== '' ? imageEuEs : imageDefault;
        }

        // For English or other languages, prefer eu-en, fallback to default
        return imageEuEn && imageEuEn.trim() !== '' ? imageEuEn : imageDefault;
      }
    }
  };

  // Track if this is the first update (to force image update even if URLs match)
  // Must be declared before updateImage() since it's referenced inside that function
  // Also track if initialization has completed to prevent resetting timing logic
  let isFirstUpdate = true;
  let hasInitialized = false;

  /**
   * Updates image based on market and language
   * Always updates to ensure correct image based on current state
   */
  const updateImage = (container) => {
    const img =
      container.querySelector('[data-market-aware-image-target]') || container.querySelector('img');
    if (!img) return;

    // Always read fresh data attributes to ensure we get the latest values
    let market = container.getAttribute('data-market');
    const imageDefault = container.getAttribute('data-image-default');
    const imageEuEn = container.getAttribute('data-image-eu-en');
    const imageEuEs = container.getAttribute('data-image-eu-es');

    // Priority 1: Check meta tag (updated by Shopify customizer when market switcher is used)
    // This works in the theme customizer preview
    const marketMeta = document.querySelector('meta[name="market-handle"]');
    if (marketMeta) {
      const marketHandle = marketMeta.getAttribute('content');
      if (marketHandle) {
        const handle = marketHandle.toLowerCase();
        // Map Shopify market handles to our internal format
        if (handle === 'european-union' || handle === 'eu' || handle.includes('europe')) {
          market = 'eu';
        } else if (
          handle === 'uk' ||
          handle === 'gb' ||
          handle === 'united-kingdom' ||
          handle === 'united kingdom'
        ) {
          market = 'uk';
        }
      }
    }

    // Priority 2: URL-based market detection (for actual page visits)
    // This works when users navigate to /en-eu or /es-eu routes
    if (!market && window.utils && window.utils.parseShopifyUrl) {
      const parsed = window.utils.parseShopifyUrl(window.location.pathname);
      if (parsed.market) {
        // Map market code to our internal format
        const urlMarket = parsed.market.toLowerCase();
        if (urlMarket === 'eu' || urlMarket === 'european-union' || urlMarket === 'europe') {
          market = 'eu';
        } else if (urlMarket === 'uk' || urlMarket === 'gb' || urlMarket === 'united-kingdom') {
          market = 'uk';
        }
      } else {
        // No market in URL means UK (default market)
        market = 'uk';
      }
    }

    // Priority 3: Fallback to data attribute from Liquid template
    if (!market) {
      market = container.getAttribute('data-market') || 'eu';
    }

    const targetImage = getTargetImage(market, imageDefault, imageEuEn, imageEuEs);

    if (!targetImage) {
      // If no target image, ensure the image is visible if it exists
      if (img.src && (!img.style.opacity || img.style.opacity === '0')) {
        img.style.opacity = '1';
      }
      return;
    }

    const currentSrc = normalizeImageUrl(img.src);
    const targetSrc = normalizeImageUrl(targetImage);

    // Always update if URLs differ, or ensure image is visible
    // Also force update if data attributes have changed (check by comparing with a stored value)
    // On first update, always check and update to ensure correct image is shown
    const dataAttributeKey = `${imageDefault || ''}|${imageEuEn || ''}|${imageEuEs || ''}`;
    const storedKey = container.getAttribute('data-last-image-key');

    // Force update on first run or if URLs differ or data attributes changed
    const shouldUpdate =
      isFirstUpdate || currentSrc !== targetSrc || storedKey !== dataAttributeKey;

    if (shouldUpdate) {
      // Store the current data attribute key to detect changes
      container.setAttribute('data-last-image-key', dataAttributeKey);

      // Ensure image is hidden before changing
      img.style.opacity = '0';

      const showImage = () => {
        img.style.opacity = '1';
      };

      // Preload the target image to ensure it's ready before showing
      const preloadImg = new Image();
      preloadImg.onload = () => {
        // Image is loaded, now update the actual img element
        requestAnimationFrame(() => {
          // Update both src and srcset to ensure correct image is shown
          const urlParts = targetImage.split('?');
          const baseUrl = urlParts[0];
          const existingParams = urlParts[1] || '';

          // Extract version from existing params if available
          const versionMatch = existingParams.match(/[&?]v=([^&]+)/);
          const version = versionMatch ? versionMatch[1] : '';

          // Build new srcset with same widths but using the correct image
          const srcsetWidths = [320, 480, 640, 768, 960, 1024, 1280, 1536, 1920, 2400];
          const newSrcset = srcsetWidths
            .map((width) => {
              // Preserve existing params but update width
              const params = existingParams
                .replace(/[&?]width=\d+/, '')
                .replace(/[&?]crop=[^&]+/, '');
              const cleanParams = params.replace(/^&/, '').replace(/&$/, '');
              const newParams = `crop=center${version ? `&v=${version}` : ''}&width=${width}${cleanParams ? `&${cleanParams}` : ''}`;
              const srcsetUrl = `${baseUrl}?${newParams}`;
              return `${srcsetUrl} ${width}w`;
            })
            .join(', ');

          img.src = targetImage;
          img.srcset = newSrcset;

          // Small delay to ensure src has updated, then show
          requestAnimationFrame(() => {
            setTimeout(showImage, 10);
          });
        });
      };
      preloadImg.onerror = () => {
        // If preload fails, still update but show immediately
        requestAnimationFrame(() => {
          // Update both src and srcset
          const urlParts = targetImage.split('?');
          const baseUrl = urlParts[0];
          const existingParams = urlParts[1] || '';
          const versionMatch = existingParams.match(/[&?]v=([^&]+)/);
          const version = versionMatch ? versionMatch[1] : '';
          const srcsetWidths = [320, 480, 640, 768, 960, 1024, 1280, 1536, 1920, 2400];
          const params = existingParams.replace(/[&?]width=\d+/, '').replace(/[&?]crop=[^&]+/, '');
          const cleanParams = params.replace(/^&/, '').replace(/&$/, '');
          const newSrcset = srcsetWidths
            .map((width) => {
              const newParams = `crop=center${version ? `&v=${version}` : ''}&width=${width}${cleanParams ? `&${cleanParams}` : ''}`;
              return `${baseUrl}?${newParams} ${width}w`;
            })
            .join(', ');

          img.src = targetImage;
          img.srcset = newSrcset;
          setTimeout(showImage, 10);
        });
      };
      preloadImg.src = targetImage;
    } else if (!img.style.opacity || img.style.opacity === '0') {
      // Image is correct but hidden, show it
      img.style.opacity = '1';
    }
  };

  /**
   * Update all market-aware images on the page
   */
  const updateAllImages = () => {
    document.querySelectorAll(IMAGE_SELECTOR).forEach(updateImage);
  };

  /**
   * Handle language/market change events
   * Now detects changes from URL (popstate) instead of relying on Weglot
   */
  const handleLanguageChange = () => {
    updateAllImages();
  };

  // Listen to URL changes (when user navigates via market/language switcher)
  window.addEventListener('popstate', handleLanguageChange);

  // Also listen to hash changes (for backward compatibility)
  window.addEventListener('hashchange', handleLanguageChange);

  // Listen to language change events using Weglot's API if available (for backward compatibility)
  if (window.Weglot && typeof window.Weglot.on === 'function') {
    window.Weglot.on('languageChanged', handleLanguageChange);
  }

  // Fallback: listen to custom DOM events (for compatibility)
  document.addEventListener('weglot:languageChanged', handleLanguageChange);

  /**
   * Listen for Shopify theme customizer section updates
   * When sections are updated in the customizer, re-run image updates
   */
  const handleSectionUpdate = () => {
    // Clear stored keys to force re-evaluation
    document.querySelectorAll(IMAGE_SELECTOR).forEach((container) => {
      container.removeAttribute('data-last-image-key');
    });
    updateAllImages();
  };

  // Listen for Shopify's section:load event (fired when section is updated in customizer)
  document.addEventListener('shopify:section:load', handleSectionUpdate);
  document.addEventListener('shopify:section:reorder', handleSectionUpdate);

  // Listen for Shopify customizer market changes
  // When market switcher is used in customizer, meta tag updates trigger image refresh
  const observeMarketMetaTag = () => {
    const marketMeta = document.querySelector('meta[name="market-handle"]');
    if (!marketMeta) return;

    const marketObserver = new MutationObserver(() => {
      // Market meta tag changed, update all images
      handleSectionUpdate();
    });

    marketObserver.observe(marketMeta, {
      attributes: true,
      attributeFilter: ['content'],
    });

    return marketObserver;
  };

  // Store the observer instance
  let dataAttributeObserver = null;

  // Use MutationObserver to detect when data attributes change
  const observeDataAttributes = () => {
    // Disconnect existing observer if it exists
    if (dataAttributeObserver) {
      dataAttributeObserver.disconnect();
    }

    dataAttributeObserver = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          (mutation.attributeName === 'data-image-default' ||
            mutation.attributeName === 'data-image-eu-en' ||
            mutation.attributeName === 'data-image-eu-es')
        ) {
          shouldUpdate = true;
        } else if (mutation.type === 'childList') {
          // New elements added, check if they're market-aware-image containers
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              // Element node
              if (node.matches && node.matches(IMAGE_SELECTOR)) {
                shouldUpdate = true;
              }
              // Also check children
              if (node.querySelectorAll) {
                const containers = node.querySelectorAll(IMAGE_SELECTOR);
                if (containers.length > 0) {
                  shouldUpdate = true;
                }
              }
            }
          });
        }
      });
      if (shouldUpdate) {
        handleSectionUpdate();
        // Re-observe in case new containers were added
        observeDataAttributes();
      }
    });

    // Observe all market-aware-image containers
    document.querySelectorAll(IMAGE_SELECTOR).forEach((container) => {
      dataAttributeObserver.observe(container, {
        attributes: true,
        attributeFilter: ['data-image-default', 'data-image-eu-en', 'data-image-eu-es'],
        childList: false,
        subtree: false,
      });
    });

    // Also observe the document body for new sections being added (more efficient: only watch for new elements)
    if (document.body) {
      dataAttributeObserver.observe(document.body, {
        attributes: false,
        childList: true,
        subtree: true,
        // Only process if the added node or its descendants match our selector
        // This is handled in the mutation callback above
      });
    }
  };

  /**
   * Initialize and run initial update
   * Only runs once to prevent resetting timing logic
   */
  const initialize = () => {
    // Prevent multiple initializations from resetting timing logic
    if (hasInitialized) {
      // Already initialized, just update images without resetting isFirstUpdate
      updateAllImages();
      return;
    }

    hasInitialized = true;
    hideInitialImages();
    isFirstUpdate = true; // Mark as first update to force image check
    updateAllImages();
    // Mark first update as complete after a short delay
    setTimeout(() => {
      isFirstUpdate = false;
    }, 1000);
    // Start observing data attribute changes
    observeDataAttributes();
    // Start observing market meta tag changes (for Shopify customizer)
    observeMarketMetaTag();
  };

  // Run initialization when DOM is ready
  const runInitialUpdate = () => {
    initialize();
  };

  // Listen for Weglot initialization if available (for backward compatibility)
  if (window.Weglot && typeof window.Weglot.on === 'function') {
    window.Weglot.on('initialized', initialize);
  }

  // Run when DOM is ready
  const runWhenReady = () => {
    // Wait a bit for utils.js to be available
    if (window.utils && window.utils.parseShopifyUrl) {
      runInitialUpdate();
    } else {
      // Retry after a short delay if utils isn't ready yet
      setTimeout(runWhenReady, 50);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runWhenReady);
  } else {
    runWhenReady();
  }
})();
